'use client'

import React from 'react'
import Link from 'next/link'
import { cn } from '@/utilities/cn'
import styles from './GlowButton.module.css'

interface GlowButtonProps {
  children: React.ReactNode
  href?: string
  className?: string
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  ariaLabel?: string
}

/**
 * GlowButton — premium CTA with animated red/gold/amethyst border glow.
 * Renders as <Link> when href is provided, otherwise as <button>.
 * The glow is a rotating conic-gradient pseudo-element that animates
 * only when the user hovers (or the component has .autoGlow).
 * Fully respects prefers-reduced-motion.
 */
export const GlowButton: React.FC<GlowButtonProps> = ({
  children,
  href,
  className,
  onClick,
  type = 'button',
  disabled,
  ariaLabel,
}) => {
  const inner = (
    <span className={styles.inner}>
      <span className={styles.arrowLeft} aria-hidden="true">&#8594;</span>
      <span className={styles.label}>{children}</span>
      <span className={styles.arrowRight} aria-hidden="true">&#8594;</span>
    </span>
  )

  if (href) {
    return (
      <Link
        href={href}
        aria-label={ariaLabel}
        className={cn(styles.btn, className)}
      >
        <span className={styles.glowRing} aria-hidden="true" />
        {inner}
      </Link>
    )
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(styles.btn, className)}
    >
      <span className={styles.glowRing} aria-hidden="true" />
      {inner}
    </button>
  )
}
